import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import axios from 'axios';
import FormContainer from './FormContainer';
import MarketoForm from './MarketoForm';
import FormLegalAdditionalText from '../components/FormLegalAdditionalText';
import FormButton from '../components/FormButton';
import handleErrorValidation from '../helpers/handleErrorValidation';
import {
  FormTitle,
  FormSubtitle,
  FormFailText
} from '../helpers/handleFormCopy';
import FormFields from '../helpers/handleFormFields';
import {
  selectFormById,
  setInitialValues,
  updateInitialTypes
} from '../helpers/handleFormLoad';
import handleFormSubmission from '../helpers/handleFormSubmission';

/* eslint-disable import/no-extraneous-dependencies */
const pmUuid = require('@postman/uuid');
/* eslint-enable */

function FormV7(data) {
  const { form } = data;
  const {
    title,
    subtitle,
    marketoForm,
    submitButton,
    padding,
    addOnLegalText,
    addOnCheckboxLabels,
    confirmation,
    mode
  } = form;
  const { id } = marketoForm;

  const [useFormData, setFormData] = useState([]);
  const [throwFailMessage, setFailMessage] = useState();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const initialValues = {};
  const fields = useFormData;
  setInitialValues(initialValues, fields);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        // eslint-disable-next-line global-require
        const response = require(`../../../../../bff-data/forms.json`);
        const selectedForm = selectFormById(response, id);
        const hasFormData = selectedForm.length !== 0;
        if (hasFormData) {
          setFormData(selectedForm);
          setFailMessage(false);
        } else {
          throw new Error();
        }
      } catch (error) {
        try {
          const { data: fallbackResponse } = await axios.get(
            `/mkapi/forms.json?t=${new Date().getTime()}`
          );
          const selectedForm = selectFormById(fallbackResponse, id);
          const hasFormData = selectedForm.length !== 0;
          setFailMessage(!hasFormData);
          setFormData(selectedForm);
        } catch (err) {
          setFailMessage(true);
        }
      }
    };

    fetchForms();
  }, [id]);

  return (
    <>
      {confirmation && formSubmitted ? (
        <div>
          <h3>{confirmation.title}</h3>
          <div
            dangerouslySetInnerHTML={{ __html: confirmation.body.join('') }}
          />
          <a href="/">← Return to Postman</a>
        </div>
      ) : (
        <FormContainer padding={padding} data-test="isFormLoading" mode={mode}>
          {fields.length > 0 ? (
            <>
              <FormTitle mode={mode} title={title} />
              <FormSubtitle mode={mode} subtitle={subtitle} />
              <Formik
                initialValues={initialValues}
                onSubmit={(values, { setSubmitting }) => {
                  handleFormSubmission(
                    id,
                    data,
                    values,
                    setSubmitting,
                    setFormSubmitted
                  );
                }}
                validationSchema={handleErrorValidation(fields)}
                enableReinitialize
              >
                <Form id={`form-${id}`} noValidate>
                  {fields.map((field) => {
                    const {
                      Id,
                      Datatype,
                      DefaultValue,
                      Htmltext,
                      IsRequired,
                      InputLabel,
                      Maxlength,
                      Name,
                      PicklistValues,
                      PlaceholderText,
                      VisibleRows,
                      VisibilityRule
                    } = field;
                    const types = updateInitialTypes(Datatype);

                    return (
                      <FormFields
                        mode={mode}
                        key={pmUuid()}
                        inputlabel={InputLabel || ''}
                        label={PlaceholderText || Name}
                        name={Name || ''}
                        mkId={Id || ''}
                        required={IsRequired || false}
                        max={Maxlength || ''}
                        type={types}
                        dropdownDefaultValue={DefaultValue || ''}
                        selectValues={PicklistValues || ''}
                        VisibilityRule={VisibilityRule || ''}
                        rows={VisibleRows || ''}
                        htmlText={Htmltext || ''}
                        addOnCheckboxLabels={
                          addOnCheckboxLabels &&
                          field.Datatype === 'single_checkbox'
                            ? addOnCheckboxLabels
                            : null
                        }
                      />
                    );
                  })}
                  <FormButton data-test="submitButton" submitButton={submitButton} />
                  {addOnLegalText && (
                    <FormLegalAdditionalText
                      mode={mode}
                      addOnLegalText={addOnLegalText}
                    />
                  )}
                </Form>
              </Formik>
            </>
          ) : null}
        </FormContainer>
      )}
      <MarketoForm data={marketoForm} />
      {throwFailMessage ? (
        <FormContainer mode={mode}>
          <FormFailText mode={mode} />
        </FormContainer>
      ) : null}
    </>
  );
}

FormV7.propTypes = {
  form: PropTypes.shape({
    title: PropTypes.string,
    padding: PropTypes.string,
    subtitle: PropTypes.arrayOf(PropTypes.string),
    marketoForm: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }).isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    submitButton: PropTypes.object,
    addOnLegalText: PropTypes.string,
    addOnCheckboxLabels: PropTypes.string,
    confirmation: PropTypes.shape({
      title: PropTypes.string,
      body: PropTypes.arrayOf(PropTypes.string)
    }),
    mode: PropTypes.string
  }).isRequired
};

export default FormV7;
